import { INSERT_TYPE, SCHEDULE_TYPE } from './constants';
import { ORDERING_CUSTOM, orderingFromRow, orderingToColumns, type OrderingChoice } from './ordering';
import { isRecurringRow, scheduleKind } from './summary';
import type { ScheduleKind, ScheduleRow } from './types';
import { END_BEFORE_START_MESSAGE, isEndBeforeStart } from '@/utils/date';
import {
    decodeDayIds,
    encodeDayIds,
    normalizeCalendarDate,
    normalizeClockTime,
    positiveInteger,
    timesCrossMidnight,
} from './values';

/**
 * The editor's write adapter: a saved row becomes a form, and a form becomes
 * the body of POST / PUT /api/custom_playlist_schedule.
 *
 * Payload keys follow LocationZoneCustomPlaylistScheduleRequest exactly. It
 * never sends effective_start_datetime, effective_end_datetime or is_recurring
 * (the database trigger owns them). Several request rules are not nullable —
 * recurring_weekly_interval, messaging_frequency_minutes, the insert_frequency_*
 * counts, loop / should_shuffle / resume_from_file_offset — so those keys are
 * left out rather than sent as null. delimited_day_ids is nullable, and a dated
 * insert, message or silence sends it as null so a saved repeat is cleared.
 */

/** Whether an insert / message / silence repeats weekly or runs on dates. */
export type ScheduleOccurrence = 'recurring' | 'single';

/** The kinds the editor can create or save. Station change is written by the player switcher. */
export const EDITABLE_SCHEDULE_KINDS = ['oneoff', 'recurring', 'insert', 'message', 'silence'] as const;
export type EditableScheduleKind = (typeof EDITABLE_SCHEDULE_KINDS)[number];

export function isEditableKind(kind: ScheduleKind): kind is EditableScheduleKind {
    return (EDITABLE_SCHEDULE_KINDS as readonly string[]).includes(kind);
}

/** Kinds that show the Priority field (as tabasco-react). */
export function kindUsesPriority(kind: ScheduleKind): boolean {
    return kind === 'oneoff' || kind === 'recurring';
}

/** Kinds that show Ordering. The players strip ordering from messages, silence and station changes. */
export function kindUsesOrdering(kind: ScheduleKind): boolean {
    return kind === 'oneoff' || kind === 'recurring' || kind === 'insert';
}

/** Kinds that choose between weekly and dated. One-off is always dated; recurring always weekly. */
export function kindHasOccurrenceChoice(kind: ScheduleKind): boolean {
    return kind === 'insert' || kind === 'message' || kind === 'silence';
}

export interface ScheduleForm {
    /** Null for a schedule that has not been saved yet. */
    id: number | null;
    kind: ScheduleKind;
    /** Only read for insert, message and silence. */
    occurrence: ScheduleOccurrence;
    ordering: OrderingChoice;
    /** Blank (null) unless the user types one. Only written for one-off and recurring. */
    priority: number | null;
    /** The priority the saved row holds, so an edit sends priority only when it changed. */
    savedPriority: number | null;
    /** Sunday = 1 … Saturday = 7. */
    dayIds: number[];
    /**
     * The day ids the saved row holds. A dated save clears them when there are
     * any, because the players decide dated vs repeating by the day ids.
     */
    savedDayIds: number[];
    /** YYYY-MM-DD */
    startDate: string | null;
    /** YYYY-MM-DD; ignored when noEndDate. */
    endDate: string | null;
    noEndDate: boolean;
    /** HH:MM:SS */
    startTime: string | null;
    /** HH:MM:SS */
    endTime: string | null;
    /** recurring_weekly_interval, 1 or more. */
    repeatWeeks: number;
    fromTracks: number;
    toTracks: number;
    rampDays: number;
    /** messaging_frequency_minutes. */
    repeatMinutes: number | null;
    /** Station change rows only, for display. */
    stationChangeLocationZonePlayerId: number | null;
    /**
     * The dates and times the saved row holds, or null for a new schedule. An end before the
     * start is refused only when the form's dates or times differ from these, as the backend
     * refuses it only when an edit changes them.
     */
    savedDatesAndTimes: ScheduleDatesAndTimes | null;
}

/** The fields that say when a schedule plays. */
export type ScheduleDatesAndTimes = Pick<ScheduleForm, 'startDate' | 'endDate' | 'noEndDate' | 'startTime' | 'endTime'>;

export interface ScheduleFormContext {
    /** The list's own zone: the zone a NEW schedule belongs to. Never sent on an edit. */
    locationZoneId: number;
    /** The list the dialog was opened from. Never sent on an edit. */
    locationZoneCustomPlaylistId: number;
}

/** The body of POST / PUT /api/custom_playlist_schedule. */
export interface SchedulePayload {
    /** Create only. An edit never moves a schedule to another zone. */
    location_zone_id?: number;
    location_zone_custom_playlist_id?: number;
    custom_playlist_schedule_type_id: number;
    silence: boolean;
    revert_to_daily?: boolean;
    priority?: number | null;
    date?: string | null;
    end_date?: string | null;
    time?: string | null;
    end_time?: string | null;
    recurring_start_date?: string | null;
    recurring_end_date?: string | null;
    recurring_weekly_interval?: number;
    /** Null clears the saved days: the row becomes dated. */
    delimited_day_ids?: string | null;
    custom_playlist_insert_type_id?: number;
    insert_frequency_tracks_start?: number;
    insert_frequency_tracks_target?: number;
    insert_frequency_ramp_days?: number;
    messaging_start_time?: string | null;
    messaging_end_time?: string | null;
    messaging_frequency_minutes?: number;
    loop?: boolean;
    should_shuffle?: boolean;
    resume_from_file_offset?: boolean;
}

/** The occurrence a form really has: one-off is dated and recurring weekly whatever the field says. */
export function formOccurrence(form: Pick<ScheduleForm, 'kind' | 'occurrence'>): ScheduleOccurrence {
    if (form.kind === 'recurring') {
        return 'recurring';
    }
    if (form.kind === 'oneoff' || form.kind === 'stationchange') {
        return 'single';
    }
    return form.occurrence;
}

/** A new schedule's defaults. Times follow tabasco-react (12:00 PM to 1:00 PM). */
export function emptyScheduleForm(kind: EditableScheduleKind = 'oneoff'): ScheduleForm {
    return {
        id: null,
        kind,
        occurrence: 'recurring',
        ordering: 'shuffled_resume',
        priority: null,
        savedPriority: null,
        dayIds: [],
        savedDayIds: [],
        startDate: null,
        endDate: null,
        noEndDate: false,
        startTime: '12:00:00',
        endTime: '13:00:00',
        repeatWeeks: 1,
        fromTracks: 1,
        toTracks: 1,
        rampDays: 1,
        repeatMinutes: 15,
        stationChangeLocationZonePlayerId: null,
        savedDatesAndTimes: null,
    };
}

/**
 * Load a saved row into the editor. Legacy rows written by tabasco-react need
 * some fallbacks: messages carry only time / end_time, and repeating inserts
 * and messages carry only `date`, so those prefill the missing fields and the
 * next save writes them properly.
 */
export function rowToScheduleForm(row: ScheduleRow): ScheduleForm {
    const kind = scheduleKind(row);
    const recurring = isRecurringRow(row);
    const messaging = kind === 'message' || kind === 'silence';
    const dayIds = decodeDayIds(row.delimited_day_ids);
    const startDate = normalizeCalendarDate(recurring ? (row.recurring_start_date ?? row.date) : row.date);
    // No fallback for the end: a repeating row without recurring_end_date plays
    // with no end, whatever its end_date says.
    const endDate = normalizeCalendarDate(recurring ? row.recurring_end_date : row.end_date);
    const startTime = normalizeClockTime(messaging ? (row.messaging_start_time ?? row.time) : row.time);
    const endTime = normalizeClockTime(messaging ? (row.messaging_end_time ?? row.end_time) : row.end_time);

    return {
        id: row.id ?? null,
        kind,
        occurrence: recurring ? 'recurring' : 'single',
        ordering: orderingFromRow(row),
        priority: row.priority ?? null,
        savedPriority: row.priority ?? null,
        dayIds,
        savedDayIds: [...dayIds],
        startDate,
        endDate,
        noEndDate: endDate === null,
        startTime,
        endTime,
        repeatWeeks: positiveInteger(row.recurring_weekly_interval) ?? 1,
        fromTracks: positiveInteger(row.insert_frequency_tracks_start) ?? 1,
        toTracks: positiveInteger(row.insert_frequency_tracks_target) ?? 1,
        rampDays: positiveInteger(row.insert_frequency_ramp_days) ?? 1,
        repeatMinutes: positiveInteger(row.messaging_frequency_minutes),
        stationChangeLocationZonePlayerId: row.station_change_location_zone_player_id ?? null,
        savedDatesAndTimes: { startDate, endDate, noEndDate: endDate === null, startTime, endTime },
    };
}

const TYPE_ID_BY_KIND: Readonly<Record<EditableScheduleKind, number>> = {
    oneoff: SCHEDULE_TYPE.DATETIME,
    recurring: SCHEDULE_TYPE.RECURRING,
    insert: SCHEDULE_TYPE.INSERT,
    message: SCHEDULE_TYPE.MESSAGING,
    silence: SCHEDULE_TYPE.MESSAGING,
};

/**
 * Build the request body. Call validateScheduleForm first; this function does
 * not re-check the rules, and it throws for a kind the editor may not save.
 *
 * An edit (form.id set) sends only what the dialog lets the person change:
 * never location_zone_id or location_zone_custom_playlist_id (saving never
 * moves a schedule to another zone or list), never revert_to_daily, and
 * priority only when it differs from the saved value (null when cleared).
 */
export function scheduleFormToPayload(form: ScheduleForm, context: ScheduleFormContext): SchedulePayload {
    const kind = form.kind;
    if (!isEditableKind(kind)) {
        throw new Error(`A ${kind} schedule cannot be saved from the editor.`);
    }
    const isCreate = form.id === null;
    const occurrence = formOccurrence(form);
    const startTime = normalizeClockTime(form.startTime);
    const endTime = normalizeClockTime(form.endTime);
    const startDate = normalizeCalendarDate(form.startDate);
    const endDate = form.noEndDate ? null : normalizeCalendarDate(form.endDate);

    const payload: SchedulePayload = {
        custom_playlist_schedule_type_id: TYPE_ID_BY_KIND[kind],
        silence: kind === 'silence',
    };

    if (isCreate) {
        payload.location_zone_id = context.locationZoneId;
        // A new silence row goes without a list: the backend attaches the zone's
        // "Silence Schedule" list, which is hidden from the player's switcher.
        // playercontrollers getlists.php sends every schedule of every list that
        // is not disabled, and leaves a hidden list that carries a silence row out
        // of the switcher while still sending the silence. So the silence plays
        // without offering a list to switch to. The list must be hidden (status 2),
        // not disabled (3): the players never read a disabled list's schedules.
        // An existing row keeps the list it is on (nothing is sent).
        if (kind !== 'silence') {
            payload.location_zone_custom_playlist_id = context.locationZoneCustomPlaylistId;
        }
        // Required on create. Left alone on update so a switcher-written value survives.
        payload.revert_to_daily = false;
    }

    // Every save writes Replace, so an edit also turns an older Insert row into Replace. The
    // interface never offers Insert.
    payload.custom_playlist_insert_type_id = INSERT_TYPE.REPLACE;

    if (kindUsesPriority(kind)) {
        const priority = positiveInteger(form.priority);
        if (isCreate || priority !== positiveInteger(form.savedPriority)) {
            payload.priority = priority;
        }
    }

    if (kind === 'message' || kind === 'silence') {
        // gvplayer reads messaging_*; time / end_time are kept equal for everything else.
        payload.messaging_start_time = startTime;
        payload.messaging_end_time = endTime;
    }
    payload.time = startTime;
    payload.end_time = endTime;

    if (occurrence === 'recurring') {
        const encodedDays = encodeDayIds(form.dayIds);
        if (encodedDays !== null) {
            payload.delimited_day_ids = encodedDays;
        }
        payload.recurring_start_date = startDate;
        payload.recurring_end_date = endDate;
        // Interval >= 1 always: getlists.php strips the recurring dates when it is empty or 0.
        payload.recurring_weekly_interval = positiveInteger(form.repeatWeeks) ?? 1;
        payload.date = null;
        payload.end_date = null;
    } else {
        payload.date = startDate;
        payload.end_date = endDate;
        // Cleared so the trigger does not treat an insert or message as repeating.
        payload.recurring_start_date = null;
        payload.recurring_end_date = null;
        // The players treat an insert or message with day ids as repeating
        // (physicalplayer common.php:6860), whatever its dates say. Leaving the
        // saved days in place would keep a "dated" insert repeating, and with
        // its recurring_start_date cleared gvplayer would never start it.
        if (kindHasOccurrenceChoice(kind) || form.savedDayIds.length > 0) {
            payload.delimited_day_ids = null;
        }
    }

    if (kind === 'insert') {
        payload.insert_frequency_tracks_start = form.fromTracks;
        payload.insert_frequency_tracks_target = form.toTracks;
        payload.insert_frequency_ramp_days = form.rampDays;
    }

    if (kind === 'message' || kind === 'silence') {
        const minutes = positiveInteger(form.repeatMinutes);
        if (minutes !== null) {
            payload.messaging_frequency_minutes = minutes;
        }
    }

    if (kindUsesOrdering(kind) && form.ordering !== ORDERING_CUSTOM) {
        Object.assign(payload, orderingToColumns(form.ordering));
    }

    return payload;
}

export type ScheduleFormField =
    | 'kind'
    | 'priority'
    | 'dayIds'
    | 'occurrence'
    | 'startDate'
    | 'endDate'
    | 'startTime'
    | 'endTime'
    | 'repeatWeeks'
    | 'fromTracks'
    | 'toTracks'
    | 'rampDays'
    | 'repeatMinutes';

export interface ScheduleFormError {
    field: ScheduleFormField;
    message: string;
}

export const SCHEDULE_FORM_MESSAGES = {
    READ_ONLY_KIND: 'Station change schedules come from the player switcher and cannot be edited here.',
    START_DATE_REQUIRED: 'Choose a start date.',
    END_DATE_REQUIRED: 'Choose an end date, or tick "No end date".',
    START_TIME_REQUIRED: 'Choose a start time.',
    END_TIME_REQUIRED: 'Choose an end time.',
    DAYS_REQUIRED: 'Choose at least one day to repeat on.',
    REPEAT_WEEKS_INVALID: 'Repeat every must be a whole number of weeks, 1 or more.',
    END_DATE_BEFORE_START: END_BEFORE_START_MESSAGE,
    RECURRING_NO_DAY_IN_RANGE: 'None of the chosen days falls between the start and end dates, so it would never play.',
    RECURRING_NEXT_DAY_ENDS_TOO_SOON:
        'This window ends the next day, so the end date must come after the last day it starts on. Otherwise the players end it before it starts.',
    DATED_END_BEFORE_START: 'The end must be on or after the start.',
    SPAN_END_NOT_AFTER_START: 'The end must be after the start. A one-off or insert that ends when it starts never plays.',
    TRACKS_INVALID: 'Track counts must be whole numbers, 1 or more.',
    FROM_BELOW_TO: "'From every' must be greater than or equal to 'To every'.",
    RAMP_INVALID: 'Ramp must be a whole number of days, 1 or more.',
    MINUTES_INVALID: 'Minutes between plays must be a whole number, 1 or more.',
    PRIORITY_INVALID: 'Priority must be a whole number, 1 or more.',
} as const;

function isWholeAtLeastOne(value: number | null | undefined): boolean {
    return typeof value === 'number' && Number.isInteger(value) && value >= 1;
}

/** "2026-10-02" plus `days`, as a calendar date. */
function addDays(date: string, days: number): string {
    const [year, month, day] = date.split('-').map(Number);
    const next = new Date(Date.UTC(year!, month! - 1, day! + days));
    return next.toISOString().slice(0, 10);
}

/** 1 for Sunday … 7 for Saturday, as delimited_day_ids stores them. */
function dayIdOf(date: string): number {
    const [year, month, day] = date.split('-').map(Number);
    return new Date(Date.UTC(year!, month! - 1, day!)).getUTCDay() + 1;
}

/**
 * Whether a repeating schedule plays at least once between its dates, as
 * tabasco-react checked. A night that crosses midnight must also finish by the
 * end date: the players expire the row at its end time on the end date
 * (gvplayer GVPlaylists.cpp:1983), so a Friday 22:00 to 02:00 row ending on
 * Friday is over at 02:00 on Friday, before it ever starts.
 */
function recurringRangeError(dayIds: number[], startDate: string, endDate: string, crossesMidnight: boolean): string | null {
    const lastStart = crossesMidnight ? addDays(endDate, -1) : endDate;
    for (let date = startDate, checked = 0; date <= lastStart && checked < 7; date = addDays(date, 1), checked += 1) {
        if (dayIds.includes(dayIdOf(date))) return null;
    }
    // A chosen day falls in the range, but only on the end date itself.
    const onEndDateOnly = crossesMidnight && dayIds.includes(dayIdOf(endDate)) && endDate >= startDate;
    return onEndDateOnly || (crossesMidnight && lastStart < startDate)
        ? SCHEDULE_FORM_MESSAGES.RECURRING_NEXT_DAY_ENDS_TOO_SOON
        : SCHEDULE_FORM_MESSAGES.RECURRING_NO_DAY_IN_RANGE;
}

/**
 * Whether the form's dates or times differ from the ones the saved row holds. A new schedule
 * has none saved, so every value it has counts as changed.
 */
function datesOrTimesChanged(form: ScheduleForm): boolean {
    const saved = form.savedDatesAndTimes;
    if (!saved) return true;
    return (
        normalizeCalendarDate(form.startDate) !== normalizeCalendarDate(saved.startDate) ||
        form.noEndDate !== saved.noEndDate ||
        (!form.noEndDate && normalizeCalendarDate(form.endDate) !== normalizeCalendarDate(saved.endDate)) ||
        normalizeClockTime(form.startTime) !== normalizeClockTime(saved.startTime) ||
        normalizeClockTime(form.endTime) !== normalizeClockTime(saved.endTime)
    );
}

/**
 * Whether the editor refuses the form because its end date is before its start date. The
 * editor shows this as the user picks, before any Save, because the end date picker cannot
 * catch a start moved past it. A saved row whose dates were already reversed is refused only
 * once the user changes its dates or times. The backend re-checks the dates only then, so an
 * edit to anything else, such as the ordering, still saves.
 */
export function formEndsBeforeStart(form: ScheduleForm): boolean {
    return (
        !form.noEndDate &&
        isEndBeforeStart(normalizeCalendarDate(form.startDate), normalizeCalendarDate(form.endDate)) &&
        datesOrTimesChanged(form)
    );
}

/**
 * The editor's rules. An empty array means the form can be saved. A window
 * whose end time is at or before its start time is allowed on a repeating
 * schedule: it ends the next day (see timesCrossMidnight).
 */
export function validateScheduleForm(form: ScheduleForm): ScheduleFormError[] {
    const errors: ScheduleFormError[] = [];
    const kind = form.kind;
    if (!isEditableKind(kind)) {
        return [{ field: 'kind', message: SCHEDULE_FORM_MESSAGES.READ_ONLY_KIND }];
    }
    const occurrence = formOccurrence(form);
    const startDate = normalizeCalendarDate(form.startDate);
    const endDate = form.noEndDate ? null : normalizeCalendarDate(form.endDate);
    const startTime = normalizeClockTime(form.startTime);
    const endTime = normalizeClockTime(form.endTime);

    if (!startDate) {
        errors.push({ field: 'startDate', message: SCHEDULE_FORM_MESSAGES.START_DATE_REQUIRED });
    }
    if (!form.noEndDate && !endDate) {
        errors.push({ field: 'endDate', message: SCHEDULE_FORM_MESSAGES.END_DATE_REQUIRED });
    }
    if (!startTime) {
        errors.push({ field: 'startTime', message: SCHEDULE_FORM_MESSAGES.START_TIME_REQUIRED });
    }
    if (!endTime) {
        errors.push({ field: 'endTime', message: SCHEDULE_FORM_MESSAGES.END_TIME_REQUIRED });
    }

    if (kindUsesPriority(kind) && form.priority !== null && !isWholeAtLeastOne(form.priority)) {
        errors.push({ field: 'priority', message: SCHEDULE_FORM_MESSAGES.PRIORITY_INVALID });
    }

    // A saved row whose end was already before its start is left alone until the user changes
    // its dates or times, as the backend does. Its other date checks are skipped with it.
    const endsBeforeStart = isEndBeforeStart(startDate, endDate);
    const refuseEndBeforeStart = endsBeforeStart && datesOrTimesChanged(form);

    if (occurrence === 'recurring') {
        if (encodeDayIds(form.dayIds) === null) {
            errors.push({ field: 'dayIds', message: SCHEDULE_FORM_MESSAGES.DAYS_REQUIRED });
        }
        if (!isWholeAtLeastOne(form.repeatWeeks)) {
            errors.push({ field: 'repeatWeeks', message: SCHEDULE_FORM_MESSAGES.REPEAT_WEEKS_INVALID });
        }
        if (endsBeforeStart) {
            if (refuseEndBeforeStart) errors.push({ field: 'endDate', message: SCHEDULE_FORM_MESSAGES.END_DATE_BEFORE_START });
        } else if (startDate && endDate && encodeDayIds(form.dayIds) !== null) {
            const rangeError = recurringRangeError(form.dayIds, startDate, endDate, timesCrossMidnight(startTime, endTime));
            if (rangeError) errors.push({ field: 'endDate', message: rangeError });
        }
    } else if (endsBeforeStart) {
        // Checked on the dates alone, so it shows before the times are picked.
        if (refuseEndBeforeStart) errors.push({ field: 'endDate', message: SCHEDULE_FORM_MESSAGES.END_DATE_BEFORE_START });
    } else {
        // A saved row whose times were already reversed on one day is left alone too, until the
        // user changes its dates or times. The backend checks the times only then.
        if (startDate && endDate && startTime && endTime && datesOrTimesChanged(form)) {
            const end = `${endDate}T${endTime}`;
            const start = `${startDate}T${startTime}`;
            // A dated one-off or insert is one continuous span, so ending at the
            // moment it starts plays nothing (physicalplayer common.php:7561-7577).
            // A dated message keeps a daily window, where equal times mean 24 hours.
            if ((kind === 'oneoff' || kind === 'insert') && end <= start) {
                errors.push({
                    field: 'endDate',
                    message: end < start ? SCHEDULE_FORM_MESSAGES.DATED_END_BEFORE_START : SCHEDULE_FORM_MESSAGES.SPAN_END_NOT_AFTER_START,
                });
            } else if (end < start) {
                errors.push({ field: 'endDate', message: SCHEDULE_FORM_MESSAGES.DATED_END_BEFORE_START });
            }
        }
    }

    if (kind === 'insert') {
        if (!isWholeAtLeastOne(form.fromTracks) || !isWholeAtLeastOne(form.toTracks)) {
            errors.push({ field: 'fromTracks', message: SCHEDULE_FORM_MESSAGES.TRACKS_INVALID });
        } else if (form.fromTracks < form.toTracks) {
            errors.push({ field: 'toTracks', message: SCHEDULE_FORM_MESSAGES.FROM_BELOW_TO });
        }
        if (!isWholeAtLeastOne(form.rampDays)) {
            errors.push({ field: 'rampDays', message: SCHEDULE_FORM_MESSAGES.RAMP_INVALID });
        }
    }

    if (kind === 'message' && !isWholeAtLeastOne(form.repeatMinutes)) {
        errors.push({ field: 'repeatMinutes', message: SCHEDULE_FORM_MESSAGES.MINUTES_INVALID });
    }
    if (kind === 'silence' && form.repeatMinutes !== null && !isWholeAtLeastOne(form.repeatMinutes)) {
        errors.push({ field: 'repeatMinutes', message: SCHEDULE_FORM_MESSAGES.MINUTES_INVALID });
    }

    return errors;
}
